import { useState, useEffect, useMemo, useCallback } from 'react';
import { Role } from '../../types';
import { adminRolesService, StrapiPermission } from '../../services/admin.service';
import { permKey, subjectToLabel, actionToShort, ACTION_PREFIX, AvailableCt } from './roleFormUtils';

export function useRolePermissions(role?: Role) {
  const [allAvailable, setAllAvailable] = useState<StrapiPermission[]>([]);
  const [enabledKeys, setEnabledKeys] = useState<Set<string>>(new Set());
  const [enabledFields, setEnabledFields] = useState<Map<string, string[]>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const loadData = async () => {
      const available = await adminRolesService.getAllAvailablePermissions();
      if (cancelled) return;
      setAllAvailable(available);

      if (role?.id) {
        const rolePerms = await adminRolesService.getRolePermissions(role.id);
        if (cancelled) return;
        const keys = new Set<string>();
        const fields = new Map<string, string[]>();
        rolePerms.forEach(p => {
          const k = permKey(p);
          keys.add(k);
          if (p.properties?.fields?.length) {
            fields.set(k, [...p.properties.fields]);
          }
        });
        setEnabledKeys(keys);
        setEnabledFields(fields);
      } else {
        setEnabledKeys(new Set());
        setEnabledFields(new Map());
      }
      setLoading(false);
    };

    loadData();
    return () => { cancelled = true; };
  }, [role]);

  const { contentTypes, pluginGroups, allPluginActions } = useMemo(() => {
    const ctMap = new Map<string, AvailableCt>();
    const pluginMap = new Map<string, string[]>();

    allAvailable.forEach(p => {
      if (p.subject && p.action.startsWith(ACTION_PREFIX)) {
        const short = actionToShort(p.action);
        if (!short) return;
        if (!ctMap.has(p.subject)) {
          ctMap.set(p.subject, {
            subject: p.subject,
            label: subjectToLabel(p.subject),
            availableActions: [],
            fieldsByAction: new Map(),
          });
        }
        const ct = ctMap.get(p.subject)!;
        if (!ct.availableActions.includes(short)) ct.availableActions.push(short);
        if (p.properties?.fields?.length) {
          ct.fieldsByAction.set(short, p.properties.fields);
        }
      } else if (!p.subject || p.subject === null) {
        const prefix = p.action.split('.').slice(0, 2).join('.');
        if (!pluginMap.has(prefix)) pluginMap.set(prefix, []);
        const arr = pluginMap.get(prefix)!;
        if (!arr.includes(p.action)) arr.push(p.action);
      }
    });

    const cts = Array.from(ctMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    const allPlugins: string[] = [];
    pluginMap.forEach(actions => actions.forEach(a => { if (!allPlugins.includes(a)) allPlugins.push(a); }));
    allPlugins.sort();

    return { contentTypes: cts, pluginGroups: pluginMap, allPluginActions: allPlugins };
  }, [allAvailable]);

  const isEnabled = useCallback((action: string, subject: string | null): boolean => {
    return enabledKeys.has(`${action}||${subject || ''}`);
  }, [enabledKeys]);

  const togglePerm = useCallback((action: string, subject: string | null) => {
    if (role?.isSystem) return;
    const k = `${action}||${subject || ''}`;
    setEnabledKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) { next.delete(k); } else { next.add(k); }
      return next;
    });
  }, [role?.isSystem]);

  const toggleAllActionsForCt = useCallback((subject: string, actions: string[]) => {
    if (role?.isSystem) return;
    const keys = actions.map(a => `${ACTION_PREFIX}${a}||${subject}`);
    const allOn = keys.every(k => enabledKeys.has(k));
    setEnabledKeys(prev => {
      const next = new Set(prev);
      if (allOn) { keys.forEach(k => next.delete(k)); }
      else { keys.forEach(k => next.add(k)); }
      return next;
    });
  }, [role?.isSystem, enabledKeys]);

  const toggleField = useCallback((action: string, subject: string, field: string) => {
    if (role?.isSystem) return;
    const k = `${action}||${subject}`;
    setEnabledFields(prev => {
      const next = new Map(prev);
      const current = next.get(k) || [];
      if (current.includes(field)) {
        next.set(k, current.filter(f => f !== field));
      } else {
        next.set(k, [...current, field]);
      }
      return next;
    });
  }, [role?.isSystem]);

  const selectAllContent = useCallback(() => {
    if (role?.isSystem) return;
    setEnabledKeys(prev => {
      const next = new Set(prev);
      contentTypes.forEach(ct => {
        ct.availableActions.forEach(a => { next.add(`${ACTION_PREFIX}${a}||${ct.subject}`); });
      });
      return next;
    });
  }, [role?.isSystem, contentTypes]);

  const deselectAllContent = useCallback(() => {
    if (role?.isSystem) return;
    setEnabledKeys(prev => {
      const next = new Set(prev);
      contentTypes.forEach(ct => {
        ct.availableActions.forEach(a => { next.delete(`${ACTION_PREFIX}${a}||${ct.subject}`); });
      });
      return next;
    });
  }, [role?.isSystem, contentTypes]);

  const selectAllPlugins = useCallback(() => {
    if (role?.isSystem) return;
    setEnabledKeys(prev => {
      const next = new Set(prev);
      allPluginActions.forEach(a => next.add(`${a}||`));
      return next;
    });
  }, [role?.isSystem, allPluginActions]);

  const deselectAllPlugins = useCallback(() => {
    if (role?.isSystem) return;
    setEnabledKeys(prev => {
      const next = new Set(prev);
      allPluginActions.forEach(a => next.delete(`${a}||`));
      return next;
    });
  }, [role?.isSystem, allPluginActions]);

  const buildPermissions = (): StrapiPermission[] => {
    const permissions: StrapiPermission[] = [];
    enabledKeys.forEach(k => {
      const [action, subject] = k.split('||');
      let fields = enabledFields.get(k) || [];
      // Content-manager permissions are ineffective without field properties;
      // when the user hasn't narrowed fields down, grant all of them
      if (fields.length === 0 && subject) {
        const short = actionToShort(action);
        const ct = contentTypes.find(c => c.subject === subject);
        if (short && ct) fields = ct.fieldsByAction.get(short) || [];
      }
      permissions.push({
        action,
        subject: subject || null,
        ...(fields.length > 0 ? { properties: { fields } } : {}),
      });
    });
    return permissions;
  };

  const contentEnabledCount = Array.from(enabledKeys).filter(k => {
    const [action] = k.split('||');
    return action.startsWith(ACTION_PREFIX);
  }).length;

  const pluginEnabledCount = Array.from(enabledKeys).filter(k => {
    const [action] = k.split('||');
    return !action.startsWith(ACTION_PREFIX);
  }).length;

  return {
    loading,
    enabledKeys,
    enabledFields,
    contentTypes,
    pluginGroups,
    allPluginActions,
    isEnabled,
    togglePerm,
    toggleAllActionsForCt,
    toggleField,
    selectAllContent,
    deselectAllContent,
    selectAllPlugins,
    deselectAllPlugins,
    buildPermissions,
    contentEnabledCount,
    pluginEnabledCount,
  };
}
